import { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import cn from 'classnames';

import { getConversations } from 'actions/getConversations';
import { seenConversation } from 'actions/seenConversation';
import { getFormattedUserName } from 'helpers/chats';

import Reply from '../Reply';
import ChatEntity from '../ChatEntity';
import Dialog from '../Dialog';
import SendMessageForm from '../SendMessageForm';
import MessengersPopup from 'components/common/MessengersPopup';
import styles from './styles.module.scss';
import { useMedia } from 'hooks/useMedia';

export const CHAT_TYPE = {
  exchangeChat: 'exchangeChat',
  singleChat: 'singleChat',
};

export default function ExchangeChat({
  onCloseClick,
  item,
  conversationId,
  type,
  employee,
  mode = CHAT_TYPE.exchangeChat,
  handleChatWindowClose,
}) {
  const { isDesktop } = useMedia();
  const [conversationItems, setConversationItems] = useState([]);
  const [roomId, setRoomId] = useState(conversationId);
  const [replyEntity, setReplyEntity] = useState(null);

  const conversations = useSelector((state) => state.conversations.items);
  const contacts = useSelector((state) => state.contacts.items);
  const notifications = useSelector((state) => state.notifications.items);
  const lang = useSelector((state) => state.settings.lang);

  const dispatch = useDispatch();
  useEffect(() => {
    if (conversationId) {
      setRoomId(conversationId);
    } else {
      setRoomId(null);
    }
  }, [conversationId]);

  useEffect(() => {
    if (roomId) {
      const conversation = conversations.find((conversation) => conversation.id === roomId);
      if (conversation && conversation.items.length > 0) {
        setConversationItems(conversation.items);
      } else {
        // TODO can be optimized by parameter in component
        const contact = contacts.find((conversation) => conversation.id === roomId);
        if (contact && contact.items.length > 0) {
          setConversationItems(contact.items);
        } else {
          setConversationItems([]);
        }
      }

      if (conversation && (conversation.is_seen === 0 || conversation.is_seen === false)) {
        dispatch(
          seenConversation({
            id: roomId,
            employee,
          }),
        );
      }
    }
  }, [roomId, conversations, contacts, item, dispatch, employee]);

  useEffect(() => {
    conversations.length === 0 && dispatch(getConversations(lang));
  }, [conversations.length, dispatch, lang]);

  useEffect(() => {
    if (notifications.length) {
      const item = notifications[notifications.length - 1];
      if (item.type === 'conversation_create') {
        dispatch(getConversations(lang));
      }
    }
  }, [notifications, dispatch, lang]);

  useEffect(() => {
    const conversation = conversations.find((conversation) => conversation.employee.id === employee.id);

    if (conversation) {
      setRoomId(conversation.id);
    } else {
      // TODO can be optimized by parameter in component
      const contact = contacts.find((contact) => contact.employee && contact.employee.id === employee.id);

      if (contact) {
        setRoomId(contact.id);
      } else {
        setRoomId(null);
      }
    }
  }, [conversations, contacts, type, employee]);

  const conversationEmployee = useMemo(() => {
    const conversation = conversations.find((conversation) => conversation.employee.id === employee.id);
    return conversation && conversation.employee;
  }, [conversations, employee]);

  const userName = useMemo(() => getFormattedUserName(employee), [employee]);

  const newConversationItem = useMemo(() => {
    if (item && conversationItems.length > 0 && conversationItems.find((convItem) => convItem.id === item.id)) {
      return null;
    } else {
      return item;
    }
  }, [conversationItems, item]);

  const handleReply = (reply) => {
    if (reply && reply.id) {
      const entity = conversationItems.find((items) => items.id === reply.id);
      if (entity) {
        setReplyEntity(entity);
      }
    } else {
      setReplyEntity(null);
    }
  };

  const avatarStyles = { backgroundImage: `url('${employee.picture}')` };

  return (
    <div className={cn(styles.chat, mode === CHAT_TYPE.singleChat && styles['chat--single'])}>
      {!isDesktop && (
        <div className={styles.chat__back_header}>
          {onCloseClick && <button className={styles.chat__back} onClick={onCloseClick} />}
        </div>
      )}
      <div className={styles.chat__header}>
        <div className={styles['chat__header-container']}>
          {isDesktop && onCloseClick && mode === CHAT_TYPE.singleChat && <button className={styles.chat__close} onClick={onCloseClick} />}
          <MessengersPopup employee={employee} popupPosition="right">
            {employee.picture ? (
              <span className={styles.chat__avatar} style={avatarStyles} />
            ) : (
              <span className={`${styles.chat__avatar} ${styles['chat__avatar--empty']}`} />
            )}
          </MessengersPopup>
          <div className={styles['chat__name-wrapper']}>
            <p className={styles.chat__name}>{userName}</p>
            <Link
              className={styles.chat__company}
              to={`/company/${employee.company?.id || employee.company_id}`}
            >
              {employee.company?.name || employee.organization_name}
            </Link>
            {employee.company?.phone && <a href={`tel:+${employee.company.phone}`} className={styles.chat__phone}>+{employee.company.phone}</a>}
          </div>
        </div>
      </div>
      {conversationItems.length > 0 && roomId && (
        <ChatEntity onReply={handleReply} data={conversationItems} conversationId={roomId} />
      )}
      {/* TODO check if employee works here */}
      <div className={styles.chat__dialog}>
        {roomId && <Dialog roomId={roomId} employee={employee} companionAvatar={employee.picture} />}
      </div>
      {replyEntity && <Reply entity={replyEntity} onRemove={handleReply} />}
      <SendMessageForm
        item={newConversationItem}
        roomId={roomId}
        user={conversationEmployee || employee}
        type={type}
        handleChatWindowClose={handleChatWindowClose}
        onReplyMessage={handleReply}
        replyEntity={replyEntity}
      />
    </div>
  );
}
